import { API_URL } from "../config/api";

/**
 * Safe JSON parsing with empty response handling
 */
const safeJsonParse = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response:', text);
    throw new Error('Invalid JSON response from server');
  }
};

export const getProjectActionItems = async (token: string, project_id: string): Promise<ProjectActionItemsInterface> => {
  try {
    const r = await fetch(`${API_URL}/action_items/${project_id}/items?size=1000`, {
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data:ProjectActionItemsInterface = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to fetch project action items');
  }
};

export const getProjectActionItemsById = async (token: string, project_id: string, item_id: string): Promise<ProjectActionItem> => {
  try {
    const r = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}`, {
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    const data:any = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to fetch project action item');
  }
}

export const createProjectActionItems = async (token: string, project_id: string, data: ProjectActionItem): Promise<ProjectActionItem> => {
  try {
    const r = await fetch(`${API_URL}/action_items/${project_id}/items`,{
      method: 'POST',
      headers: {
        'Content-Type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    })
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    const res:any = await r.json();
    return res;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to create project action item');
  }
}

export const updateProjectActionItems = async (token: string, project_id: string, item_id: string, data: ProjectActionItem): Promise<ProjectActionItem> => {
  try {
    const res = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    })

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    const responseData = await safeJsonParse(res);
    
    // If server returns empty response, return the updated data
    if (!responseData) {
      return {
        ...data,
        id: item_id
      } as ProjectActionItem;
    }

    return responseData;
  } catch(e: Error | any) {
    if (e.message?.includes('JSON')) {
      throw new Error('Server returned invalid response. The action item may have been updated successfully.');
    }
    throw new Error(e.message || 'Failed to update project action item');
  }
};

export const deleteProjectActionItems = async (token: string, project_id: string, item_id: string): Promise<void> => {
  try {
    const r = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}`,{
      method: 'DELETE',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // DELETE operations typically don't return content, so we don't try to parse JSON
    return;
  } catch(e: Error | any) {
    throw new Error(e.message || 'Failed to delete project action item');
  }
};

export const createProjectActionItemDocument = async (
  token: string,
  project_id: string,
  item_id: string,
  file: File,
  description?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data:any = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create project action item document');
  }
};

export const getProjectActionItemDocument = async (
  token: string,
  project_id: string,
  item_id: string,
  file_name?: string
): Promise<any> => {
  try {
    const url = file_name 
      ? `${API_URL}/action_items/${project_id}/items/${item_id}/document/${file_name}`
      : `${API_URL}/action_items/${project_id}/items/${item_id}/document`;

    const r = await fetch(url, {
      method: 'GET',
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // Check if response is JSON (metadata) or file content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    } else {
      // Return the response for file download
      return r;
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get project action item document');
  }
};

export const updateProjectActionItemDocument = async (
  token: string,
  project_id: string,
  item_id: string,
  file: File,
  description?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'PUT',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data:any = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update project action item document');
  }
};

export const deleteProjectActionItemDocument = async (
  token: string,
  project_id: string,
  item_id: string
): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/action_items/${project_id}/items/${item_id}/document`, {
      method: 'DELETE',
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // DELETE may return JSON response or empty
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    }
    
    return null;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to delete project action item document');
  }
};
